// One intent, one key and expected_version. A 202 is queued, never proof of release or playback.
// Ambiguous receipts reconcile the same intent; only fresh, user-specific participant evidence permits release.
import type { Call } from '@bcio-voice/contracts';
import { type ApiRequestError, type VoicemailDropsClient, isApiRequestError } from '../../lib/api.ts';
import { commandPollDelay, newIdempotencyKey } from '../../lib/backoff.ts';
import type { DropOption } from '../voicemail/drops.ts';
import { type LocalDrop, type LocalDropStore, dropEligibility, dropLineReleased, localDrops } from './voicemailDrop.ts';

export interface DropCommandDeps {
  client: Pick<VoicemailDropsClient, 'drop' | 'command'>;
  upsert: (call: Call) => void;
  refetchCall: (callId: string) => Promise<void>;
  userId: string;
  getCall?: (callId: string) => Call | undefined;
  store?: LocalDropStore;
  newKey?: () => string;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  maxPolls?: number;
}

/** Plain words for the drop command's refusals; any other code shows the server's own (safe) message. */
export const DROP_REFUSAL_COPY: Readonly<Record<string, string>> = Object.freeze({
  // B11's codes (apps/api/src/voicemail-drops/command.ts and the controller's refusals).
  voicemail_drop_outbound_pstn_only: 'Voicemail drop is for calls you place to outside numbers.',
  voicemail_drop_not_active: 'The call has to be connected and not on hold.',
  voicemail_drop_in_progress: 'A voicemail drop is already in progress on this call.',
  voicemail_drop_budget_exhausted: 'This call has reached its time or spending limit, so the drop can’t play.',
  voicemail_drop_budget_unverified: 'The phone system couldn’t confirm this call’s limits, so the drop can’t play.',
  voicemail_drop_audio_invalid: 'That drop’s audio couldn’t be played. Choose another, or ask an admin to upload it again.',
  voicemail_drop_schema_unavailable: 'Voicemail drops aren’t installed on the server yet.',
  voicemail_drop_already_used: 'A voicemail drop has already been requested on this call.',
  voicemail_drop_already_dropped: 'A voicemail drop has already been requested on this call.',
  voicemail_drop_not_answered: 'The call hasn’t been answered yet.',
  call_not_answered: 'The call hasn’t been answered yet.',
  voicemail_drop_call_held: 'Resume the call first. A voicemail can’t be dropped while it’s on hold.',
  call_held: 'Resume the call first. A voicemail can’t be dropped while it’s on hold.',
  voicemail_drop_emergency: 'Voicemail drop is never available on emergency calls.',
  emergency_call: 'Voicemail drop is never available on emergency calls.',
  voicemail_drop_monitored: 'Not available while a coach is on this call.',
  call_monitored: 'Not available while a coach is on this call.',
  voicemail_drop_not_outbound: 'Voicemail drop is for calls you place to outside numbers.',
  voicemail_drop_wrong_line: 'That drop isn’t in this number’s library or yours. Choose another.',
  voicemail_drop_not_found: 'That drop was deleted. Choose another.',
  not_found: 'That drop or this call isn’t available any more.',
  version_conflict: 'The call changed just then. Check it, then try again.',
  state_conflict: 'The call changed just then. Check it, then try again.',
  spend_limit_reached: 'This call has reached its spending limit, so the drop can’t play.',
  permission_denied: 'Your role can’t drop voicemail on this call.',
  voicemail_drops_schema_unavailable: 'Voicemail drops aren’t installed on the server yet.',
  rate_limited: 'Too many requests. Wait a moment, then try again.',
});

const STATUSES = new Set(['accepted', 'dispatching', 'applied', 'failed', 'unknown']);

function refusalText(error: ApiRequestError | null, fallback: string): string {
  if (!error) return fallback;
  return DROP_REFUSAL_COPY[error.code] ?? (error.message || fallback);
}

function readableCall(value: unknown): value is Call {
  if (!value || typeof value !== 'object') return false;
  const c = value as Call;
  const nullableText = (v: unknown) => v === null || typeof v === 'string';
  const timestamp = (v: unknown) => typeof v === 'string' && Number.isFinite(Date.parse(v));
  const nullableTime = (v: unknown) => v === null || timestamp(v);
  return typeof c.id === 'string' && Number.isInteger(c.version) && c.version > 0
    && ['created', 'ringing', 'active', 'held', 'dropping', 'ended', 'failed'].includes(c.state)
    && ['inbound', 'outbound', 'internal'].includes(c.direction) && nullableText(c.outcome) && nullableText(c.contact_id)
    && !!c.from && nullableText(c.from.number_id) && nullableText(c.from.e164) && nullableText(c.from.display_name)
    && !!c.destination && (c.destination.kind === 'pstn' ? typeof c.destination.e164 === 'string' : c.destination.kind === 'internal' && typeof c.destination.user_id === 'string')
    && timestamp(c.created_at) && nullableTime(c.answered_at) && nullableTime(c.ended_at)
    && [c.talk_ms, c.hold_ms, c.queue_ms].every(n => Number.isFinite(n) && n >= 0)
    && typeof c.recording_state === 'string' && typeof c.transcription_state === 'string'
    && Array.isArray(c.allowed_actions) && c.allowed_actions.every(a => typeof a === 'string')
    && Array.isArray(c.participants) && c.participants.every(p => p && typeof p.id === 'string'
      && ['agent', 'external', 'supervisor'].includes(p.kind) && nullableText(p.user_id) && nullableTime(p.joined_at) && nullableTime(p.left_at))
    && (c.voicemail_drop === undefined || !!c.voicemail_drop && typeof c.voicemail_drop.drop_id === 'string'
      && typeof c.voicemail_drop.name === 'string' && timestamp(c.voicemail_drop.started_at) && nullableTime(c.voicemail_drop.ended_at)
      && Number.isFinite(c.voicemail_drop.played_ms) && typeof c.voicemail_drop.result === 'string');
}

function receiptOf(body: unknown): { commandId: string | null; status: string | null; call: Call | null } {
  const data = typeof body === 'object' && body !== null ? (body as { data?: unknown }).data : undefined;
  const r = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
  const call = r.call as Call | undefined;
  return {
    commandId: typeof r.command_id === 'string' ? r.command_id : null,
    status: typeof r.status === 'string' ? r.status : null,
    call: readableCall(call) ? call : null,
  };
}

/** Requests whose outcome is still open, by call (the key is reused only to reconcile that same tap). */
interface Flight { key: string; dropId: string; version: number; lastVersion: number; commandId: string | null; polls: number; scheduled: boolean; polling: boolean }
const requests = new WeakMap<LocalDropStore, Map<string, Flight>>();
function flights(store: LocalDropStore): Map<string, Flight> {
  let map = requests.get(store);
  if (!map) { map = new Map(); requests.set(store, map); }
  return map;
}

/**
 * Requests `option` on `call`. Queued/releasing remain pending. `started` requires this agent's departure
 * in a fresh server record; the dock independently checks local audio before removing the card.
 */
export async function sendDrop(deps: DropCommandDeps, call: Call, option: Pick<DropOption, 'id' | 'name' | 'duration_ms'>): Promise<LocalDrop> {
  const store = deps.store ?? localDrops;
  const now = deps.now ?? Date.now;
  const existing = store.get(call.id);
  if (existing && existing.phase !== 'failed') return existing;
  const key = (deps.newKey ?? (() => newIdempotencyKey('web')))();
  const record: LocalDrop = { callId: call.id, dropId: option.id, name: option.name, durationMs: option.duration_ms, phase: 'sending', at: now(), error: null, requestId: null, userId: deps.userId, requestedVersion: call.version };
  store.set(record);
  flights(store).set(call.id, { key, dropId: option.id, version: call.version, lastVersion: call.version, commandId: null, polls: 0, scheduled: false, polling: false });
  await post(deps, call.id);
  return store.get(call.id) ?? record;
}

/** "Check again" after an unanswered send: the identical request with the same key, which the server answers once. */
export async function checkDropAgain(deps: DropCommandDeps, callId: string): Promise<LocalDrop | null> {
  const store = deps.store ?? localDrops;
  const flight = flights(store).get(callId);
  const current = store.get(callId);
  if (!flight || !current || current.phase !== 'unknown') return current;
  if (current.userId !== deps.userId || flight.polling) return current;
  flight.polls = 0;
  store.patch(callId, { phase: 'sending', error: null });
  if (flight.commandId) await poll(deps, callId);
  else await post(deps, callId);
  return store.get(callId);
}

/** Forgets a refused drop so the control offers it again. */
export function dismissDrop(callId: string, store: LocalDropStore = localDrops): void {
  if (store.get(callId)?.phase === 'failed') {
    store.clear(callId);
    flights(store).delete(callId);
  }
}

async function post(deps: DropCommandDeps, callId: string, retried = false): Promise<void> {
  const store = deps.store ?? localDrops;
  const flight = flights(store).get(callId);
  if (!flight) return;
  try {
    const body = await deps.client.drop(callId, flight.dropId, flight.version, flight.key);
    apply(deps, callId, receiptOf(body));
  } catch (error) {
    const e = isApiRequestError(error) ? error : null;
    if (!e || e.outcomeUnknown) {
      store.patch(callId, { phase: 'unknown', error: `${e?.message || 'The server didn’t answer.'} The drop may or may not have started.`, requestId: e?.requestId ?? null });
      void deps.refetchCall(callId).catch(() => undefined);
      return;
    }
    if (!retried && e.status === 409 && e.code === 'version_conflict') {
      await deps.refetchCall(callId).catch(() => undefined);
      const refreshed = deps.getCall?.(callId);
      if (refreshed && refreshed.version > flight.version && dropEligibility(refreshed).enabled) {
        flight.key = (deps.newKey ?? (() => newIdempotencyKey('web')))();
        flight.version = refreshed.version;
        flight.lastVersion = refreshed.version;
        store.patch(callId, { requestedVersion: refreshed.version });
        await post(deps, callId, true);
        return;
      }
    }
    store.patch(callId, { phase: 'failed', error: refusalText(e, 'The phone system didn’t accept the drop.'), requestId: e.requestId });
    flights(store).delete(callId);
    void deps.refetchCall(callId).catch(() => undefined);
  }
}

function apply(deps: DropCommandDeps, callId: string, receipt: ReturnType<typeof receiptOf>): void {
  const store = deps.store ?? localDrops;
  const flight = flights(store).get(callId);
  if (!flight) return;
  const validCommand = receipt.commandId && (!flight.commandId || receipt.commandId === flight.commandId);
  if (validCommand) flight.commandId = receipt.commandId;
  const cached = deps.getCall?.(callId);
  const currentVersion = Math.max(flight.lastVersion, cached && !dropLineReleased(cached, store.get(callId), deps.userId) ? cached.version : flight.version);
  const call = receipt.call;
  const validCall = call && call.id === callId && call.version >= currentVersion
    && (call.version > flight.version || !call.voicemail_drop)
    && (!call.voicemail_drop || call.voicemail_drop.drop_id === flight.dropId);
  if (!validCommand || !validCall || !receipt.status || !STATUSES.has(receipt.status)) {
    store.patch(callId, { phase: 'unknown', error: 'The drop receipt couldn’t be verified. Your line’s release and playback are not confirmed.' });
    void deps.refetchCall(callId).catch(() => undefined);
    schedule(deps, callId);
    return;
  }
  flight.lastVersion = Math.max(flight.lastVersion, call.version);
  if (!cached || call.version >= cached.version) deps.upsert(call);
  const latestCall = cached && cached.version > call.version ? cached : call;
  if (receipt.status === 'failed') {
    store.patch(callId, { phase: 'failed', error: 'The voicemail drop command failed. Delivery is not confirmed.' });
    flights(store).delete(callId);
    void deps.refetchCall(callId).catch(() => undefined);
    return;
  }
  if (receipt.status === 'unknown') {
    store.patch(callId, { phase: 'unknown', error: 'The drop outcome is unknown. Checking with the phone system…' });
    schedule(deps, callId);
    return;
  }
  if (dropLineReleased(latestCall, store.get(callId), deps.userId)) {
    store.patch(callId, { phase: 'started', error: null });
    if (receipt.status === 'applied') flights(store).delete(callId);
    else schedule(deps, callId);
    return;
  }
  store.patch(callId, { phase: receipt.status === 'accepted' || latestCall.state === 'created' || latestCall.state === 'ringing' ? 'queued' : 'releasing', error: null });
  void deps.refetchCall(callId).catch(() => undefined);
  schedule(deps, callId);
}

function schedule(deps: DropCommandDeps, callId: string): void {
  const store = deps.store ?? localDrops;
  const flight = flights(store).get(callId);
  if (!flight?.commandId || flight.scheduled) return;
  if (flight.polls >= (deps.maxPolls ?? 8)) {
    store.patch(callId, { phase: 'unknown', error: 'The drop is still unconfirmed. Check again for its current status.' });
    return;
  }
  const setTimer = deps.setTimer ?? ((fn: () => void, wait: number) => setTimeout(fn, wait));
  flight.scheduled = true;
  setTimer(() => {
    if (flights(store).get(callId) !== flight) return;
    flight.scheduled = false;
    void poll(deps, callId);
  }, commandPollDelay(flight.polls));
}

async function poll(deps: DropCommandDeps, callId: string): Promise<void> {
  const store = deps.store ?? localDrops;
  const flight = flights(store).get(callId);
  if (!flight?.commandId || flight.polling) return;
  flight.polling = true;
  flight.polls += 1;
  try {
    apply(deps, callId, receiptOf(await deps.client.command(flight.commandId)));
  } catch (error) {
    const e = isApiRequestError(error) ? error : null;
    store.patch(callId, { phase: 'unknown', error: e?.message || 'Couldn’t read the drop’s current status. Release and playback are not confirmed.', requestId: e?.requestId ?? null });
    schedule(deps, callId);
  } finally {
    flight.polling = false;
  }
}
